import type { WebSockets } from '@pushflow/types'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import crossCircleIcon from '../../../assets/images/icons/cross_circle.svg'
import { useAuthWebSocket, useModal } from '../../../hooks'
import {
  type ECurrency,
  type EGameType,
  EModalName,
  type ETableLimit,
} from '../../../types/enums'
import { trpc } from '../../../utils'
import { Icon, Loader } from '../../atoms'
import { TableListElement } from '../../molecules'
import { tableLimitsByBigBlind } from './constants'
import { iconStyle, tableContainer, tablesBlock } from './index.css'

export const TablesList = (props: {
  tab: ECurrency
  type: 'PUBLIC' | 'PRIVATE'
  gameType?: EGameType
  limits?: ETableLimit[]
  sitNow?: boolean
  onClickSit: (table: WebSockets.TableType) => void
}) => {
  const { mutateAsync: updateTableState } =
    trpc.table.updateTableState.useMutation()
  const { openInfoModal, openConfirmationModal } = useModal()
  const { sendJsonMessage, lastJsonMessage, isAuthorizied } =
    useAuthWebSocket<WebSockets.LobbyWebSocketOutputType>('lobby')
  const [tables, setTables] = useState<WebSockets.TablesType | null>(null)
  const limits = props.limits || []

  useEffect(() => {
    if (isAuthorizied) {
      sendJsonMessage({
        type: 'init',
        data: {
          type: props.type,
          currency: props.tab,
          gameType: props.gameType,
        },
      })
    }
  }, [props.tab, props.gameType, isAuthorizied, sendJsonMessage, props.type])

  useEffect(() => {
    if (lastJsonMessage === null) {
      return
    }
    console.log(lastJsonMessage)

    if (lastJsonMessage.type === 'initial') {
      setTables(lastJsonMessage.data)
      return
    }

    if (lastJsonMessage.type === 'update') {
      const playerCount = lastJsonMessage.data.playerCount
      if (playerCount !== undefined) {
        if (!tables || !tables[lastJsonMessage.data.tableId]) {
          return
        }
        const updatedTable = tables[lastJsonMessage.data.tableId]
        setTables((tables) => ({
          ...tables,
          [lastJsonMessage.data.tableId]: {
            ...updatedTable,
            playerCount,
          },
        }))
        return
      }
      if (lastJsonMessage.data.action === 'TABLE_DELETED') {
        if (tables) {
          const newTables = {
            ...tables,
          }
          delete newTables[lastJsonMessage.data.tableId]
          setTables(newTables)
        }
        return
      }
    }
  }, [lastJsonMessage])

  const onClickDeleteTable = async (tableId: string) => {
    const { closeModal } = openConfirmationModal(
      {
        text: 'Are you sure you want to delete this table? This action cannot be undone.',
        mainButtonText: 'Delete',
      },
      {
        title: 'Delete table',
        name: EModalName.CONFIRM_TABLE_DELETE,
      },
    )
    const result = await closeModal
    if (result !== 'main') {
      return
    }
    try {
      const response = await updateTableState({
        tableId,
        delete: true,
      })
      if (response.success) {
        openInfoModal(
          {
            text: 'The delete command was sent successfully. The table will be deleted at the start of the next hand.',
            buttonText: 'Ok',
          },
          {
            title: 'Success',
            name: EModalName.INFO_TABLE_DELETE_SUCCESSFULLY,
          },
        )
        return
      }
    } catch (error) {
      if (!(error instanceof Error)) {
        throw error
      }
      openInfoModal(
        {
          text: error.message,
          buttonText: 'Close',
        },
        {
          title: 'Error',
          name: EModalName.INFO_TABLE_STATE_UPDATE_ERROR,
        },
      )
    }
  }

  if (tables === null) {
    return <Loader state={true} />
  }

  return (
    <div className={tablesBlock}>
      {tables
        ? Object.values(tables).map((table) => {
            if (props.sitNow && table.playerCount >= table.maxPlayers) {
              return null
            }
            if (limits.length > 0 && table.currency in tableLimitsByBigBlind) {
              const tableLimit = Object.entries(
                tableLimitsByBigBlind[
                  table.currency as
                    | typeof ECurrency.USDT
                    | typeof ECurrency.COIN
                ],
              ).find(([limit, treshold]) => {
                if (table.bigBlind < treshold) {
                  return true
                }
              })
              if (
                tableLimit &&
                !limits.includes(tableLimit[0] as ETableLimit)
              ) {
                return null
              }
            }
            return (
              <motion.div
                key={table.id}
                className={tableContainer}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                {table.isHost ? (
                  <Icon
                    src={crossCircleIcon}
                    className={iconStyle}
                    analytic={{
                      name: 'delete_table',
                    }}
                    onClick={() => {
                      void onClickDeleteTable(table.id)
                    }}
                  />
                ) : null}
                <TableListElement
                  {...table}
                  onClickSit={() => props.onClickSit(table)}
                />
              </motion.div>
            )
          })
        : null}
    </div>
  )
}
